import logo from "@assets/logo.png";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { AppleSignInButton } from "@web/components/AppleSignInButton";
import { GoogleSignInButton } from "@web/components/GoogleSignInButton";
import { Button, Field } from "@web/components/ui";
import { useEffect, useState } from "react";
import { useAuth, useToast } from "@/contexts";
import { signUpWithInvite, verifyInviteCode } from "@/features/access/api";
import { signInWithApple } from "@/features/auth/api";
import { getProviderConnections, hasConnectedProvider } from "@/features/sync/connections";

export default function SignUp() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/sign-up" }) as { redirect?: string; code?: string };
  const [codeInput, setCodeInput] = useState(search.code ?? "");
  const [verifying, setVerifying] = useState(false);
  const [verifiedCode, setVerifiedCode] = useState<string | null>(null);
  const [invitedByName, setInvitedByName] = useState<string | null>(null);
  const [codeError, setCodeError] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [socialSigning, setSocialSigning] = useState(false);
  const { refreshSession, setSession } = useAuth();
  const toast = useToast();

  // Invite codes only create the appearance of exclusivity — a bad, expired,
  // or unknown code still lets someone through. Only a genuine request
  // failure blocks progress here.
  async function verifyCode(rawCode: string) {
    if (!rawCode.trim() || verifying) return;
    setVerifying(true);
    setCodeError("");
    const { data, error } = await verifyInviteCode(rawCode.trim());
    setVerifying(false);
    if (error || !data) {
      setCodeError("Couldn't check that code. Try again.");
      return;
    }
    setVerifiedCode(rawCode.trim());
    setInvitedByName(data.valid ? data.invitedByName : null);
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: only re-run when the ?code= param changes
  useEffect(() => {
    if (search.code) void verifyCode(search.code);
  }, [search.code]);

  async function handleAppleSignUp() {
    if (socialSigning) return;
    setSocialSigning(true);
    const { error } = await signInWithApple();
    setSocialSigning(false);
    if (error) toast.showAlert("Sign-in failed", error.message, "error");
  }

  async function continueAfterAuth(): Promise<void> {
    const connections = await getProviderConnections();
    if (hasConnectedProvider(connections)) {
      navigate({ to: search.redirect ?? "/dashboard", replace: true });
    } else {
      navigate({
        to: "/health-setup",
        search: { redirect: search.redirect ?? "/dashboard" },
        replace: true,
      });
    }
  }

  function onGoogleSignUpSuccess(): void {
    toast.showAlert("Account created", "Welcome to FootSlapping!", "success");
    void continueAfterAuth();
  }

  function onGoogleSignUpError(error: string): void {
    toast.showAlert("Sign-up failed", error, "error");
  }

  async function handleSignUp() {
    if (!verifiedCode) return;
    if (!name.trim() || !email.trim() || !password.trim() || !confirmPassword.trim()) {
      toast.showAlert("Required", "Fill in all fields.", "error");
      return;
    }
    if (password !== confirmPassword) {
      toast.showAlert("Validation", "Passwords do not match.", "error");
      return;
    }
    if (password.length < 8) {
      toast.showAlert("Validation", "Password must be at least 8 characters.", "error");
      return;
    }
    setLoading(true);
    const { data, error } = await signUpWithInvite(
      verifiedCode,
      email.trim(),
      password,
      name.trim(),
    );
    setLoading(false);
    if (error) {
      toast.showAlert("Sign-up failed", error.message, "error");
    } else {
      toast.showAlert("Account created", "Welcome to FootSlapping!", "success");
      // Hydrate from the sign-up response's own user instead of
      // re-verifying through refreshSession's `/auth/session` call, which
      // can lose a read-after-write race against the session row just
      // created and falsely report it as expired.
      if (data?.token && data.user) {
        setSession(data.user, data.token);
      } else {
        await refreshSession();
      }
      await continueAfterAuth();
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-[26rem] bg-card rounded-[1.25rem] border border-border shadow-[1px_1px_0px_0px_#1c1b2e] p-6 sm:p-8 flex flex-col gap-5">
        <div className="flex flex-col items-center gap-3 text-center">
          <img src={logo} alt="FootSlapping" className="w-16 h-16 object-contain" />
          <div>
            <span className="text-text-dim text-[0.625rem] font-bold tracking-widest uppercase">
              Invite only
            </span>
            <h1 className="text-text text-2xl font-extrabold tracking-tight mt-1">
              Create account
            </h1>
            <p className="text-text-muted text-sm mt-1">
              {verifiedCode
                ? invitedByName
                  ? `You were invited by ${invitedByName}. Welcome!`
                  : "Your invite code checks out. Welcome!"
                : "Enter your invite code to get started"}
            </p>
          </div>
        </div>

        {!verifiedCode ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void verifyCode(codeInput);
            }}
            className="flex flex-col gap-4"
          >
            <Field
              label="Invite code"
              value={codeInput}
              onChangeText={setCodeInput}
              placeholder="e.g. ABC123XY"
            />
            {codeError ? (
              <p className="text-coral text-xs font-semibold" role="alert">
                {codeError}
              </p>
            ) : null}
            <Button
              type="submit"
              label={verifying ? "Checking..." : "Verify code"}
              disabled={verifying || !codeInput.trim()}
            />
            <p className="text-text-muted text-xs text-center">
              Don't have a code?{" "}
              <Link
                to="/request-access"
                className="text-aqua font-extrabold no-underline hover:underline"
              >
                Request access
              </Link>
            </p>
          </form>
        ) : (
          <>
            <div className="flex flex-col gap-3">
              <GoogleSignInButton
                text="signup_with"
                width="100%"
                onSuccess={onGoogleSignUpSuccess}
                onError={onGoogleSignUpError}
              />
              <AppleSignInButton onPress={handleAppleSignUp} disabled={socialSigning} />
            </div>

            <div className="relative flex items-center">
              <div className="flex-1 border-t border-border-light" />
              <span className="mx-3 text-text-muted text-xs">or</span>
              <div className="flex-1 border-t border-border-light" />
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                void handleSignUp();
              }}
              className="flex flex-col gap-4"
            >
              <Field label="Name" value={name} onChangeText={setName} placeholder="Your name" />
              <Field
                label="Email"
                value={email}
                onChangeText={setEmail}
                placeholder="you@example.com"
                type="email"
              />
              <Field
                label="Password"
                value={password}
                onChangeText={setPassword}
                placeholder="At least 8 characters"
                secureTextEntry
              />
              <Field
                label="Confirm password"
                value={confirmPassword}
                onChangeText={setConfirmPassword}
                placeholder="Re-enter password"
                secureTextEntry
              />
              <Button
                type="submit"
                label={loading ? "Creating account..." : "Create account"}
                disabled={loading}
              />
            </form>
          </>
        )}

        <p className="text-text-muted text-xs text-center">
          Already have an account?{" "}
          <Link to="/sign-in" className="text-aqua font-extrabold no-underline hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
